/**
 * clearFusionCMS script to manage sidebar position.
 *
 * THIS SOFTWARE IS PROVIDED "AS IS" AND WITHOUT ANY EXPRESS OR IMPLIED
 * WARRANTIES, INCLUDING, WITHOUT LIMITATION, THE IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE.
 *
 * @package clearFusionCMS
 * @copyright Copyright (c) 2015 - 2023 clearFusionCMS. All rights reserved.
 * @link http://clearFusionCMS.com
 */

import Alpine from "alpinejs";
import {} from "./core/fusionCSS.fam.js";
import {} from "./core/fusionCSS.floatingLabels.js";
import {} from "./core/fusionCSS.slideInMenu.js";
import {} from "./core/fusionCSS.tabs.js";
import {} from "./core/fusionCSS.toast.js";
import {} from "./core/fusionCSS.utils.js";
import {} from "./fusionCSSDragOrder.js";
import {} from "./fusionCSSTagInput.js";
import {} from "./fusionCSSTrackElem.js";

function getNoticeIcon(type) {
	if(type == 1)
		return 'fa-info-circle info';
	if(type == 2)
		return 'fa-exclamation-triangle warning';
	if(type == 3)
		return 'fa-ban error';
	return 'fa-info-circle info';
}

Alpine.data('cfAdminShell', () => ({
	mainMenuExposed: false,
	mainMenuPanelOpen: false,
	activeMainMenu: null,

	init() {
		this.$nextTick(() => {
			const level2 = document.getElementById('mainMenuLevel2');
			if(level2)
				level2.querySelectorAll('ul').forEach((menu) => menu.setAttribute('aria-hidden', 'true'));

			const menu = document.getElementById('mainMenu');
			if(menu) {
				menu.querySelectorAll('li').forEach((item) => {
					const title = item.getAttribute('title') || '';
					item.dataset.menutitle = title;
					item.setAttribute('title', '');
					item.addEventListener('click', () => this.selectMainMenu(item));
					item.addEventListener('mouseenter', () => this.showMainMenuTip(item));
					item.addEventListener('mouseleave', () => this.hideMainMenuTip());
				});
			}
		});
	},

	toggleMainMenu() {
		this.mainMenuExposed = !this.mainMenuExposed;
		if(!this.mainMenuExposed)
			this.mainMenuPanelOpen = false;
	},

	closeMainMenu() {
		this.mainMenuExposed = false;
		this.mainMenuPanelOpen = false;
		this.activeMainMenu = null;
		document.querySelectorAll('#mainMenu li').forEach((item) => item.classList.remove('active'));
		document.querySelectorAll('#mainMenuLevel2 ul').forEach((menu) => {
			menu.classList.remove('active');
			menu.setAttribute('aria-hidden', 'true');
		});
	},

	closeOverlays() {
		this.closeMainMenu();
		window.dispatchEvent(new CustomEvent('cf-close-topbar-panels'));
	},

	selectMainMenu(item) {
		const menuName = item.getAttribute('data-mainmenu');
		if(!menuName) {
			this.closeMainMenu();
			return;
		}

		const menu = document.getElementById(menuName);
		if(!menu)
			return;

		const alreadyOpen = this.mainMenuPanelOpen && menu.classList.contains('active');
		document.querySelectorAll('#mainMenu li').forEach((navItem) => navItem.classList.remove('active'));
		document.querySelectorAll('#mainMenuLevel2 ul').forEach((panel) => {
			panel.classList.remove('active');
			panel.setAttribute('aria-hidden', 'true');
		});

		if(alreadyOpen) {
			this.mainMenuPanelOpen = false;
			this.activeMainMenu = null;
			return;
		}

		item.classList.add('active');
		menu.classList.add('active');
		menu.setAttribute('aria-hidden', 'false');
		this.activeMainMenu = menuName;
		this.mainMenuPanelOpen = true;
		this.mainMenuExposed = true;
	},

	showMainMenuTip(item) {
		if(window.innerWidth <= 767 || this.mainMenuPanelOpen)
			return;

		const tip = this.$refs.mainMenuTip;
		if(!tip)
			return;

		tip.style.display = 'block';
		tip.style.top = `${item.getBoundingClientRect().top + window.scrollY}px`;
		tip.textContent = item.dataset.menutitle || '';
	},

	hideMainMenuTip() {
		const tip = this.$refs.mainMenuTip;
		if(tip)
			tip.style.display = 'none';
	}
}));

Alpine.data('cfAdminTopbar', () => ({
	searchOpen: false,
	filtersOpen: false,

	init() {
		window.addEventListener('cf-close-topbar-panels', () => this.closePanels());
		this.$nextTick(() => {
			this.$root.querySelectorAll('.cmsFilterShow').forEach((button) => {
				button.addEventListener('click', (event) => {
					event.preventDefault();
					this.toggleFilters();
				});
			});
		});
	},

	toggleSearch() {
		this.searchOpen = !this.searchOpen;
		this.filtersOpen = this.searchOpen ? this.filtersOpen : false;
	},

	toggleFilters() {
		this.filtersOpen = !this.filtersOpen;
		this.searchOpen = this.filtersOpen ? this.searchOpen : false;
	},

	closePanels() {
		this.searchOpen = false;
		this.filtersOpen = false;
	},
}));

Alpine.data('cfAdminNotifications', () => ({
	open: false,
	notices: [],
	count: 0,
	timer: null,

	init() {
		this.load();
		this.timer = window.setInterval(() => this.load(), 45000);
	},

	toggle() {
		if(this.count > 0)
			this.open = !this.open;
		else
			this.open = false;
	},

	close() {
		this.open = false;
	},

	async load() {
		if(typeof window.managerPath === 'undefined')
			return;

		try {
			const response = await fetch(`${window.managerPath}getnotices/`, {
				headers: {
					'Accept': 'application/json',
					'X-Requested-With': 'XMLHttpRequest'
				}
			});
			const data = await response.json();
			if(!data.hasNotices) {
				this.notices = [];
				this.count = 0;
				this.open = false;
				return;
			}

			let nextCount = 0;
			this.notices = data.notices.map((notice, index) => {
				const noticeCount = notice.count === null ? null : Number(notice.count);
				nextCount += noticeCount === null ? 1 : noticeCount;
				return {
					key: `${index}-${notice.type}-${notice.text}`,
					type: notice.type,
					icon: getNoticeIcon(notice.type),
					count: noticeCount,
					text: notice.text,
					link: notice.link
				};
			});
			this.count = nextCount;
			if(this.count === 0)
				this.open = false;
		}
		catch(e) {
			this.notices = [];
			this.count = 0;
			this.open = false;
		}
	}
}));

Alpine.data('cfFileBrowser', () => ({
	init() {
		this.$nextTick(() => this.update());
		window.addEventListener('scroll', () => this.update());
		window.addEventListener('resize', () => this.update());
		window.addEventListener('load', () => this.update());
	},

	update() {
		const treepane = this.$refs.treepane;
		const treepaneInner = this.$refs.treepaneInner;
		if(!treepane || !treepaneInner)
			return;

		const height = window.innerHeight;
		if(window.innerWidth < 768) {
			treepaneInner.style.width = '100%';
			document.querySelectorAll('.treeviewwidgetContainer').forEach((container) => {
				container.style.height = `${height / 2}px`;
			});
		}
		else {
			treepaneInner.style.width = `${treepane.getBoundingClientRect().width}px`;
			document.querySelectorAll('.treeviewwidgetContainer').forEach((container) => {
				container.style.height = `${height}px`;
			});
		}
	}
}));

window.Alpine = Alpine;
if(document.readyState === 'loading')
	document.addEventListener('DOMContentLoaded', () => Alpine.start());
else
	Alpine.start();

jQuery(function() {
	var sidebar = $('#sidebar'),
		sidebarContent = $('#sidebar .content'),
		sidebarTree = $('#sidebar .treeviewwidgetContainer'),
		sidebarHeader = $('#sidebar .header'),
		sideContainer = $('#sideContainer'),
		sidebarCard = $('#sidebar .card'),
		appbar = $('.appbar'),
		submitList = $('#submit-list');
		win = $(window);

	function updateSidebar() {
		try {
			if(win.width() > 767) {

				var appbarHgt = appbar.height() +
						parseInt(appbar.css('padding-top')) +
						parseInt(appbar.css('padding-bottom')),
					toolbarHgt,
					submitListHgt,
					offset = Math.max(appbarHgt - sidebarCard[0].offsetTop, sideContainer[0].offsetTop - win.scrollTop());

				if(sidebarHeader.length) {
					toolbarHgt = sidebarHeader.height() +
							parseInt(sidebarHeader.css('padding-top')) +
							parseInt(sidebarHeader.css('padding-bottom'));
				}
				else
					toolbarHgt = 0;

				if(submitList.length && submitList.css('position') == 'fixed') {
					submitListHgt = submitList.height() +
						parseInt(submitList.css('padding-top')) +
						parseInt(submitList.css('padding-bottom'));
				}
				else
					submitListHgt = 0;

				// Set position of sidebar
				sidebar
					.addClass('sidebarFixed')
					.css('left', sideContainer[0].offsetLeft + parseInt(sideContainer.css('padding-left')) + 'px')
					.css('width', sideContainer.width() + 'px')
					.css('top', offset + 'px');

				sidebarContent.css('height', (win.height() - toolbarHgt - submitListHgt - offset - sidebarCard[0].offsetTop) + 'px');
				sidebarTree.css('height', (win.height() - toolbarHgt - submitListHgt - offset - sidebarCard[0].offsetTop) + 'px');
			}
			else {
				sidebar
					.removeClass('sidebarFixed')
					.css('top', 'inherit')
					.css('left', 'auto')
					.css('width', 'auto');
				sidebarContent.css('height', 'auto');
				sidebarTree.css('height', 'auto');
			}
		} catch(e) {
		}
	}

	win.on('scroll', updateSidebar)
		.on('resize', updateSidebar)
		.on('load', updateSidebar);
	updateSidebar();

	// Add working indicator to form submit
	$('form').on('submit', function(e) {
		$('#submit-list input').addClass('disabled');
		$('#submit-list button').addClass('disabled');
		$.toastHide();
		$('.alertfade').addClass('hide');
	}).on('formValidationFailed', function(e) {
		$('#submit-list input').removeClass('disabled');
		$('#submit-list button').removeClass('disabled');
	});

	if(typeof managerPath !== 'undefined') {
		// Find the number of open tabs
		var openTabs = 0;
		$(window).on('storage', function(evt) {

			// If got a ping command send a response
			if(evt.originalEvent.key == '_cfCMS_ManagerOpenPing') {
				localStorage.setItem('_cfCMS_ManagerOpenResponse', evt.originalEvent.newValue);
				localStorage.removeItem('_cfCMS_ManagerOpenResponse');
			}
			// If got a response
			else if(evt.originalEvent.key == '_cfCMS_ManagerOpenResponse' && evt.originalEvent.newValue == null) {
				openTabs++;
			}
		});

		// Ping other tabs
		localStorage.setItem('_cfCMS_ManagerOpenPing', Date.now());

		// Notice polling is now handled by the Alpine notification component.
	}

	/**
	 * Size file manager container windows
	 */
	if($('iframe.filemanager').length) {
		function sizeFMContainer() {
			var bottomSpace = parseInt($('.page-wrapper').css('padding-bottom')),
				top = $('#mainContent').offset().top +
			$('#mainContent .header').outerHeight(true) +
			parseInt($('#mainContent .content').css('padding-top')) +
			parseInt($('#mainContent .content').css('padding-bottom')) +
			parseInt($('#mainContent .pageBlock').css('margin-bottom')) + 5;

			$('iframe.filemanager').height($(window).height() - top - bottomSpace);
		}

		$(window).on('resize', sizeFMContainer);
		sizeFMContainer()
	}

	// Capture delete sequence
	var deleteConfirmed = $('#deleteConfirmed'),
		deleteUnconfirmed = $('#deleteUnconfirmed');
	if(deleteConfirmed.length) {
		deleteConfirmed.css('display', 'none');
		deleteUnconfirmed.on('click', function(evt) {
			evt.preventDefault();
			evt.stopPropagation();

			deleteUnconfirmed.css('display', 'none');
			deleteConfirmed.css('display', 'inline-block').attr('disabled', 'disabled');

			setTimeout(function() {
				deleteConfirmed.attr('disabled', null);
			}, 250);
		});
		$('body').on('click', function(evt) {
			if(deleteConfirmed.css('display') != 'none' && !$(evt.target).closest('#deleteConfirmed').length) {
				evt.preventDefault();
				evt.stopPropagation();

				deleteUnconfirmed.css('display', 'inline-block');
				deleteConfirmed.css('display', 'none');
			}
		});
	}
});

(function() {
	// Update focus to force scroll
	var extend = window.jQuery || window.fusionLib;
	var fnFocus = extend.fn.focus;
	extend.fn.focus = function() {
		fnFocus.call(this);

		// Find the label container
		var e = extend(this[0]),
			l = extend('#' + e.attr('id') + '-lblcontainer');

		if(l.length) {
			var top = l.offset().top;
			if(top <= extend(window).scrollTop() || top > (extend(window).scrollTop() + (extend(window).height() * .5))) {
				var barh = extend('.appbar').length ? extend('.appbar').height() : 0;
				extend(window).scrollTop(top - barh - 20);
			}
		}

		return this;
	};

})();

// Capture content builder tool expose events
$(window).on('contentBuilderToolOpen', function(evt, d) {
	if(d)
		$('.singleColPage').addClass('cbToolsShown');
	else
		$('.singleColPage').removeClass('cbToolsShown');
});
